/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RoutingService } from 'src/app/service/routing.service';
import { AuthService } from 'src/app/service/auth.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { describeReferences } from '../query-delete/query-delete.component';

/** What deleting the collection takes with it (see askDelete). */
interface DeletePlan {
  exclusive: { uid: string }[];
  shared: number;
  usedBy: { kind: string; name: string }[];
}

@Component({
  selector: 'app-query-name',
  templateUrl: './query-name.component.html',
  styleUrls: ['./query-name.component.scss'],
})
export class QueryNameComponent {
  namefolio: UntypedFormControl;
  namequery: UntypedFormControl;
  deletePlan: DeletePlan | null = null;
  deleting = false;
  collectionsList = [];
  selectedCollections = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private router: Router,
    public routing: RoutingService,
    public auth: AuthService,
    public featureFlagsService: FeatureFlagsService,
    private queryApi: QueryApiService,
    public snackBar: MatSnackBar,
  ) {
    this.queryApi.getGroupsQuery().subscribe((result) => {
      this.collectionsList = result;
    });

    this.namefolio = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]);

    const queryName = this.queryApi.query?.name || '';
    this.namequery = new UntypedFormControl(queryName, [
      Validators.required,
      Validators.minLength(3),
    ]);

    if (
      this.data &&
      this.data.action !== 'create' &&
      this.data.action !== 'add'
    ) {
      this.namefolio.setValue(this.data.name);
    }
  }

  iSave() {
    if (this.namefolio.valid) {
      if (this.data) {
        this.queryApi
          .editGroupQuery(this.namefolio.value, this.data.uid)
          .subscribe(() => {
            this.dialog.closeAll();
            this.router.navigate([], {
              queryParams: {
                update: Math.random(),
              },
              queryParamsHandling: 'merge',
            });
          });
      } else {
        if (
          this.collectionsList.length <
          this.featureFlagsService.featureFlags.collectionsLimit
        ) {
          this.queryApi
            .addGroupQuery(this.namefolio.value)
            .subscribe((result) => {
              this.dialog.closeAll();
              this.router.navigate([], {
                queryParams: {
                  id: result.id,
                  groupId: result.id,
                  update: Math.random(),
                },
                queryParamsHandling: 'merge',
              });
            });
        } else {
          this.snackBar.open(
            'Number of collections is limited to ' +
              this.featureFlagsService.featureFlags.collectionsLimit +
              '.',
            'OK',
            { duration: 5000 },
          );
        }
      }
    }
  }

  iCancel() {
    this.dialog.closeAll();
  }

  /**
   * Queries only in this collection go with it; shared ones stay in their
   * other collections, so deleting never strands a query in "Other".
   */
  askDelete() {
    // The guard refreshes `querys` on every navigation; `data` is a snapshot.
    const collection =
      this.queryApi.querys?.find((c) => c.uid === this.data.uid) ?? this.data;
    const others = new Set(
      (this.queryApi.querys ?? [])
        .map((c) => c.uid)
        .filter((uid) => uid !== collection.uid),
    );
    const content = collection.content ?? [];
    // Without `groups` membership is unknown; keep the query.
    const exclusive = content.filter(
      (q) =>
        Array.isArray(q.groups) && !q.groups.some((uid) => others.has(uid)),
    );
    this.deletePlan = {
      exclusive,
      shared: content.length - exclusive.length,
      usedBy: describeReferences(
        exclusive.flatMap((q) => q.referenced_by ?? []),
      ),
    };
  }

  confirmDelete() {
    if (this.deleting) return;
    this.deleting = true;
    const uids = this.deletePlan.exclusive.map((q) => q.uid);
    // Queries first: if that fails, the collection is still intact.
    (uids.length ? this.queryApi.delQueries(uids) : of(null))
      .pipe(switchMap(() => this.queryApi.deleteGroupQuery(this.data.uid)))
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          this.leaveDeletedCollection();
        },
        error: () => {
          this.deleting = false;
          this.deletePlan = null;
          this.snackBar.open(
            'Could not delete the collection. Please try again.',
            'OK',
            { duration: 5000 },
          );
          // Reload so a retry plans against what is left.
          this.router.navigate([], {
            queryParams: { update: Math.random() },
            queryParamsHandling: 'merge',
          });
        },
      });
  }

  /** Without an id the query guard opens the first remaining collection. */
  private leaveDeletedCollection() {
    this.router.navigate(['/cockpit/news/query/main'], {
      queryParams: {
        id: null,
        groupId: null,
        queryid: null,
        newsfeedFromQuery: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * create portfolio with provided entity
   */
  iCreate() {
    if (this.namefolio.valid && this.namequery.valid) {
      if (
        this.collectionsList.length <
        this.featureFlagsService.featureFlags.collectionsLimit
      ) {
        this.queryApi
          .addGroupQuery(this.namefolio.value)
          .subscribe((result) => {
            const data = {
              groups: [result.id],
              name: this.namequery.value,
              description: this.queryApi.query.description,
              query: this.queryApi.query.query,
            };
            this.queryApi.addQuery(data).subscribe(() => {
              this.queryApi.query = {
                ...this.queryApi.query,
                name: this.namequery.value,
              };
              this.dialog.closeAll();
              this.snackBar.open(
                'Collection ' + this.namefolio.value + ' created.',
                'OK',
                { duration: 5000 },
              );
            });
          });
      } else {
        this.snackBar.open(
          'Number of collections is limited to ' +
            this.featureFlagsService.featureFlags.collectionsLimit +
            '.',
          'OK',
          { duration: 5000 },
        );
      }
    }
  }

  /**
   * add query to selected collections
   */
  iAdd() {
    if (this.namequery.valid && this.selectedCollections.length > 0) {
      const data = {
        groups: this.selectedCollections,
        name: this.namequery.value,
        description: this.queryApi.query.description,
        query: this.queryApi.query.query,
      };
      const targetCollectionId = this.selectedCollections[0];
      this.queryApi.addQuery(data).subscribe(() => {
        this.queryApi.query = {
          ...this.queryApi.query,
          name: this.namequery.value,
        };
        this.dialog.closeAll();
        const snackRef = this.snackBar.open(
          'Query added to selected collections.',
          'View Collection',
          { duration: 5000, panelClass: ['view-query-snackbar'] },
        );
        snackRef.onAction().subscribe(() => {
          this.router.navigate(['/cockpit/news/query'], {
            queryParams: {
              id: targetCollectionId,
              groupId: targetCollectionId,
              update: Math.random(),
            },
            queryParamsHandling: 'merge',
          });
        });
      });
    }
  }
}
